/** The only ruleset that is eligible for the online leaderboard. */
export const RULESET_ID = 'namco-rev-b-v1' as const;
export const GAME_VERSION = RULESET_ID;

export const DIP_BASELINE = {
  startingLives: 3,
  bonusLifeFirst: 20_000,
  bonusLifeEvery: 70_000,
  bonusLifeCap: 1_000_000,
  difficulty: 'easy',
  demoSounds: true,
  freeze: false,
  cabinet: 'upright',
  coinage: '1 coin / 1 credit',
  playerOneCounterDigits: 6,
  playerTwoCounterDigits: 7,
} as const;

export const SCORE = {
  beeFormation: 50,
  beeFlight: 100,
  butterflyFormation: 80,
  butterflyFlight: 160,
  bossFormation: 150,
  bossAttack: [400, 800, 1600],
  challengeTarget: 100,
  challengePerfect: 10_000,
  morphTarget: 160,
  morphBonus: { scorpion: 1_000, bosconian: 2_000, flagship: 3_000 },
} as const;

export type GameMode = 'single-player' | 'alternating-two-player';
export type RoundKind = 'normal' | 'challenging';
export type EnemyKind = 'bee' | 'butterfly' | 'boss';
export type MorphKind = keyof typeof SCORE.morphBonus;
export type ChallengeTargetKind = 'dragonfly' | 'satellite' | 'starship';
export type AttackPattern = 'sweep' | 'scissors' | 'spiral' | 'convoy' | 'cannon';

export interface FormationUnit {
  readonly id: number;
  readonly kind: EnemyKind;
  readonly row: number;
  readonly column: number;
  readonly leader: boolean;
  readonly entryWave: number;
}

export interface MorphGroup {
  readonly kind: MorphKind;
  readonly source: 'bee' | 'butterfly';
  readonly triggerWave: number;
  readonly targets: 3;
}

export interface ChallengeRoute {
  readonly target: ChallengeTargetKind;
  readonly route: readonly [number, number][];
  readonly wave: number;
  readonly points: 100;
}

export interface RoundDescriptor {
  readonly round: number;
  readonly kind: RoundKind;
  readonly roster: readonly FormationUnit[];
  readonly entryWaves: readonly (readonly number[])[];
  readonly leaderCount: number;
  readonly attackPattern: AttackPattern;
  readonly attackFrequencyTicks: number;
  readonly diveSpeed: number;
  readonly aggression: number;
  readonly cannonIntervalTicks: number;
  readonly morphs: readonly MorphGroup[];
  readonly challengeRoutes: readonly ChallengeRoute[];
  readonly post255: 'arcade-boundary' | 'web-safe-local-only';
}

const CHALLENGE_TARGETS: readonly ChallengeTargetKind[] = ['dragonfly', 'satellite', 'starship'];
const ATTACK_PATTERNS: readonly AttackPattern[] = ['sweep', 'scissors', 'spiral', 'convoy', 'cannon'];
const MORPH_KINDS: readonly MorphKind[] = ['scorpion', 'bosconian', 'flagship'];

// The formation is always 40 targets in the normal Namco Rev. B baseline.
// Leader positions are the top four columns; later aggression changes their
// attack/entry schedule rather than changing the number of formation slots.
const BASE_FORMATION: readonly FormationUnit[] = [
  ...Array.from({ length: 4 }, (_, column) => ({ id: column, kind: 'boss' as const, row: 0, column, leader: true, entryWave: 0 })),
  ...Array.from({ length: 8 }, (_, column) => ({ id: 4 + column, kind: 'butterfly' as const, row: 1, column, leader: false, entryWave: 1 })),
  ...Array.from({ length: 8 }, (_, column) => ({ id: 12 + column, kind: 'butterfly' as const, row: 2, column, leader: false, entryWave: 2 })),
  ...Array.from({ length: 10 }, (_, column) => ({ id: 20 + column, kind: 'bee' as const, row: 3, column, leader: false, entryWave: 3 })),
  ...Array.from({ length: 10 }, (_, column) => ({ id: 30 + column, kind: 'bee' as const, row: 4, column, leader: false, entryWave: 4 })),
];

const CHALLENGE_PATHS: readonly (readonly [number, number][])[] = [
  [[-0.12, 0.18], [0.08, 0.28], [0.32, 0.18], [0.55, 0.35], [0.78, 0.16], [1.12, 0.30]],
  [[1.12, 0.16], [0.88, 0.33], [0.63, 0.14], [0.39, 0.34], [0.15, 0.16], [-0.12, 0.31]],
  [[-0.10, 0.42], [0.18, 0.20], [0.42, 0.46], [0.66, 0.20], [0.94, 0.44], [1.10, 0.24]],
];

function leaderCountFor(round: number): number {
  // The Rev. B service routine promotes additional drone slots in the later
  // aggression bands. Keeping this table explicit makes the round contract
  // inspectable and replayable instead of hiding it in a difficulty scalar.
  if (round < 5) return 4;
  if (round < 9) return 5;
  if (round < 17) return 6;
  if (round < 33) return 7;
  return 8;
}

function rosterFor(round: number): FormationUnit[] {
  const leaders = leaderCountFor(round);
  return BASE_FORMATION.map((unit, index) => {
    if (unit.kind === 'boss' || index < leaders) return { ...unit, leader: unit.kind === 'boss' || index < leaders };
    return unit;
  });
}

function entryWavesFor(round: number, roster: readonly FormationUnit[]): readonly (readonly number[])[] {
  const waveCount = 5 + (round >= 17 ? 1 : 0) + (round >= 33 ? 1 : 0);
  const waves = Array.from({ length: waveCount }, () => [] as number[]);
  for (const unit of roster) waves[Math.min(unit.entryWave, waveCount - 1)]?.push(unit.id);
  return waves;
}

function challengeRoutesFor(round: number): readonly ChallengeRoute[] {
  if (round % 4 !== 3 || round > 255) return [];
  return Array.from({ length: 40 }, (_, index) => ({
    target: CHALLENGE_TARGETS[(round + index) % CHALLENGE_TARGETS.length] ?? 'dragonfly',
    route: CHALLENGE_PATHS[index % CHALLENGE_PATHS.length] ?? CHALLENGE_PATHS[0]!,
    wave: index,
    points: 100 as const,
  }));
}

function morphsFor(round: number): readonly MorphGroup[] {
  if (round < 5 || round % 4 === 3) return [];
  const kind = MORPH_KINDS[(round - 5) % MORPH_KINDS.length] ?? 'scorpion';
  return [{ kind, source: round % 2 === 0 ? 'butterfly' : 'bee', triggerWave: 2 + (round % 3), targets: 3 }];
}

/** Returns the complete deterministic round descriptor for rounds 1..255. */
export function createRoundDescriptor(round: number): RoundDescriptor {
  if (!Number.isInteger(round) || round < 1 || round > 255) throw new RangeError('round must be an integer from 1 through 255');
  const kind: RoundKind = round % 4 === 3 ? 'challenging' : 'normal';
  const roster = rosterFor(round);
  return {
    round,
    kind,
    roster,
    entryWaves: kind === 'normal' ? entryWavesFor(round, roster) : [],
    leaderCount: kind === 'normal' ? leaderCountFor(round) : 0,
    attackPattern: ATTACK_PATTERNS[(round - 1) % ATTACK_PATTERNS.length] ?? 'sweep',
    attackFrequencyTicks: Math.max(36, 156 - round * 2 - (round >= 80 ? 24 : 0)),
    diveSpeed: 1.45 + Math.min(1.9, round * 0.012),
    aggression: Math.min(1, 0.18 + round / 320),
    cannonIntervalTicks: Math.max(120, 600 - round * 2),
    morphs: morphsFor(round),
    challengeRoutes: challengeRoutesFor(round),
    post255: 'arcade-boundary',
  };
}

export function stageBadgeDenominations(round: number): number[] {
  let remaining = Math.max(1, round);
  const result: number[] = [];
  for (const denomination of [50, 30, 20, 10, 5, 1]) {
    while (remaining >= denomination) {
      result.push(denomination);
      remaining -= denomination;
    }
  }
  return result;
}

export function isChallengingRound(round: number): boolean {
  return round >= 3 && round <= 255 && round % 4 === 3;
}

export function scoreForEnemy(kind: EnemyKind, context: 'formation' | 'flight', escorts = 0): number {
  if (kind === 'bee') return context === 'formation' ? SCORE.beeFormation : SCORE.beeFlight;
  if (kind === 'butterfly') return context === 'formation' ? SCORE.butterflyFormation : SCORE.butterflyFlight;
  if (context === 'formation') return SCORE.bossFormation;
  return SCORE.bossAttack[Math.min(2, Math.max(0, escorts))] ?? SCORE.bossAttack[0];
}

export function scoreCounter(score: number, playerSlot: 1 | 2): number {
  const modulus = playerSlot === 1 ? 1_000_000 : 10_000_000;
  return Math.max(0, Math.floor(score)) % modulus;
}

export function shouldAwardBonus(scoreBefore: number, scoreAfter: number): number {
  if (scoreBefore >= DIP_BASELINE.bonusLifeCap) return 0;
  let awarded = 0;
  for (let threshold = DIP_BASELINE.bonusLifeFirst; threshold <= scoreAfter && threshold < DIP_BASELINE.bonusLifeCap; threshold += threshold === DIP_BASELINE.bonusLifeFirst ? DIP_BASELINE.bonusLifeEvery - DIP_BASELINE.bonusLifeFirst : DIP_BASELINE.bonusLifeEvery) {
    if (threshold > scoreBefore) awarded++;
  }
  return awarded;
}
